// Persistence boundary for the staff-edited Mass Member Credentials email.
//
// Staff can edit only the introduction, the closing and the signature. The
// subject, greeting, login credentials and chamber name stay fixed, so the
// backend keeps rendering them from the template and the recipient.
//
// Backend: query getCmpMassCredentialEmailEdits and mutation
// saveCmpMassCredentialEmailEdits (template 'member-dashboard-credentials-mass',
// saved per chamber, separate from Single Send Credentials and activation).
// The saved text is part of the credential plan fingerprint, so the caller
// reloads the plan after a save. Keep the component on these functions so the
// wiring stays in one place.

import { ApolloClient } from "@apollo/client";
import {
  CmpMassCredentialEmailBlockKindEnum,
  CmpMassCredentialEmailEditsFieldsFragment,
  GetCmpMassCredentialEmailEditsDocument,
  GetCmpMassCredentialEmailEditsQuery,
  GetCmpMassCredentialEmailEditsQueryVariables,
  SaveCmpMassCredentialEmailEditsDocument,
  SaveCmpMassCredentialEmailEditsMutation,
  SaveCmpMassCredentialEmailEditsMutationVariables,
} from "../../../../graphql/types";
import { CALLER_HANDLED_USER_ERROR_CONTEXT } from "../../../../utils/apolloCallerContext";

export type MassCredentialEmailBlock =
  | { kind: "paragraph"; text: string }
  | { kind: "list"; items: string[] };

export type MassCredentialEmailEdits = {
  intro: MassCredentialEmailBlock[];
  closing: MassCredentialEmailBlock[];
  signatureName: string;
  signatureEmail: string;
  signaturePhone: string;
};

type MassCredentialEmailClient = Pick<ApolloClient<object>, "query" | "mutate">;

export type LoadMassCredentialEmailEditsInput = {
  chamberId: string;
  /** The Apollo client from `useApolloClient()` in the calling component. */
  client?: MassCredentialEmailClient;
};

export type SaveMassCredentialEmailEditsInput = {
  chamberId: string;
  edits: MassCredentialEmailEdits;
  /** The Apollo client from `useApolloClient()` in the calling component. */
  client?: MassCredentialEmailClient;
};

export const massCredentialEmailEditsUnavailableMessage =
  "Email editing is not available yet. The email will be sent as shown in the preview.";

export class MassCredentialEmailEditsUnavailableError extends Error {
  constructor() {
    super(massCredentialEmailEditsUnavailableMessage);
    this.name = "MassCredentialEmailEditsUnavailableError";
  }
}

const fromApiEdits = (
  edits: CmpMassCredentialEmailEditsFieldsFragment,
): MassCredentialEmailEdits => {
  const toBlocks = (blocks: CmpMassCredentialEmailEditsFieldsFragment["intro"]) =>
    blocks.map((block): MassCredentialEmailBlock =>
      block.kind === CmpMassCredentialEmailBlockKindEnum.List
        ? { kind: "list", items: [...(block.items || [])] }
        : { kind: "paragraph", text: block.text || "" });
  return {
    intro: toBlocks(edits.intro),
    closing: toBlocks(edits.closing),
    signatureName: edits.signatureName || "",
    signatureEmail: edits.signatureEmail || "",
    signaturePhone: edits.signaturePhone || "",
  };
};

const toApiBlocks = (blocks: MassCredentialEmailBlock[]) =>
  blocks.map((block) =>
    block.kind === "list"
      ? { kind: CmpMassCredentialEmailBlockKindEnum.List, items: block.items }
      : { kind: CmpMassCredentialEmailBlockKindEnum.Paragraph, text: block.text });

/**
 * Returns the current editable parts of the chamber's mass credential email:
 * the saved edits, or the template defaults when nothing was saved.
 */
export const loadMassCredentialEmailEdits = async ({
  chamberId,
  client,
}: LoadMassCredentialEmailEditsInput): Promise<MassCredentialEmailEdits> => {
  if (!client) throw new MassCredentialEmailEditsUnavailableError();
  const result = await client.query<
    GetCmpMassCredentialEmailEditsQuery,
    GetCmpMassCredentialEmailEditsQueryVariables
  >({
    query: GetCmpMassCredentialEmailEditsDocument,
    variables: { chamberId },
    fetchPolicy: "network-only",
    context: CALLER_HANDLED_USER_ERROR_CONTEXT,
  });
  const edits = result.data?.getCmpMassCredentialEmailEdits;
  if (!edits) throw new Error("The email text could not be loaded.");
  return fromApiEdits(edits);
};

/**
 * Saves the editable parts for the chamber and returns the stored values.
 * The backend includes the saved values in the credential plan identity,
 * so the caller reloads the plan before any send.
 */
export const saveMassCredentialEmailEdits = async ({
  chamberId,
  client,
  edits,
}: SaveMassCredentialEmailEditsInput): Promise<MassCredentialEmailEdits> => {
  if (!client) throw new MassCredentialEmailEditsUnavailableError();
  const normalized = normalizeMassCredentialEmailEdits(edits);
  const result = await client.mutate<
    SaveCmpMassCredentialEmailEditsMutation,
    SaveCmpMassCredentialEmailEditsMutationVariables
  >({
    mutation: SaveCmpMassCredentialEmailEditsDocument,
    variables: {
      input: {
        chamberId,
        edits: {
          intro: toApiBlocks(normalized.intro),
          closing: toApiBlocks(normalized.closing),
          signatureName: normalized.signatureName,
          signatureEmail: normalized.signatureEmail || null,
          signaturePhone: normalized.signaturePhone || null,
        },
      },
    },
    context: CALLER_HANDLED_USER_ERROR_CONTEXT,
  });
  const saved = result.data?.saveCmpMassCredentialEmailEdits;
  if (!saved) throw new Error("Your email changes could not be saved.");
  return fromApiEdits(saved);
};

const collapseSpaces = (value: string) => value.replace(/[^\S\n]+/g, " ").trim();

const normalizeBlocks = (blocks: MassCredentialEmailBlock[]): MassCredentialEmailBlock[] =>
  blocks.flatMap((block): MassCredentialEmailBlock[] => {
    if (block.kind === "paragraph") {
      const text = block.text
        .split("\n")
        .map(collapseSpaces)
        .filter(Boolean)
        .join("\n");
      return text ? [{ kind: "paragraph", text }] : [];
    }
    const items = block.items.map(collapseSpaces).filter(Boolean);
    return items.length ? [{ kind: "list", items }] : [];
  });

/** Removes empty blocks and extra white space that contentEditable adds. */
export const normalizeMassCredentialEmailEdits = (
  edits: MassCredentialEmailEdits,
): MassCredentialEmailEdits => ({
  intro: normalizeBlocks(edits.intro),
  closing: normalizeBlocks(edits.closing),
  signatureName: collapseSpaces(edits.signatureName),
  signatureEmail: collapseSpaces(edits.signatureEmail),
  signaturePhone: collapseSpaces(edits.signaturePhone),
});

export const areMassCredentialEmailEditsEqual = (
  left: MassCredentialEmailEdits,
  right: MassCredentialEmailEdits,
) =>
  JSON.stringify(normalizeMassCredentialEmailEdits(left)) ===
  JSON.stringify(normalizeMassCredentialEmailEdits(right));

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Returns the first problem that blocks a save, or null. */
export const validateMassCredentialEmailEdits = (
  edits: MassCredentialEmailEdits,
): string | null => {
  const normalized = normalizeMassCredentialEmailEdits(edits);
  if (!normalized.intro.length) return "Enter the email introduction before you save.";
  if (!normalized.signatureName) return "Enter the signature name before you save.";
  if (normalized.signatureEmail && !emailPattern.test(normalized.signatureEmail)) {
    return "Enter a full email address for the signature, for example name@example.com.";
  }
  return null;
};
